import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { observer } from "mobx-react";
import type { Swagpack } from "../../../../../../models/Swagstore/Packs";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { fileNameFromUrl } from "../../../../../../utils/urlHelper";
import { getPackDisplayImage } from "../../../../../../utils/packImageUtils";
import cdricon from "../../../../../../assets/cdrIcon.png";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import ImageUpload from "../../../../../../views/components/common/upload/ImageUpload";
import ImageAPI from "../../../../api/ImageAPI";
import AppDropDown from "../../../../../../views/components/common/filter/AppDropDown";
import {
  catalogSkuChoices,
  catalogSkuForRequest,
  mockupMatchesCatalogSku,
  PRODUCT_LEVEL_SKU,
} from "./catalogSkuChoices";

type Props = {
  open: boolean;
  product: Swagpack | null;
  storeId: string | number | null | undefined;
  onClose: () => void;
};

const StorePrintingFilesDialog: React.FC<Props> = observer(
  ({ open, product, storeId, onClose }) => {
    const { mockupConfigStore } = useStores();
    const { showError, showSuccess } = useNotification();
    const productId = product?.id ? Number(product.id) : null;
    const resolvedStoreId =
      storeId != null && String(storeId).trim() !== ""
        ? String(storeId)
        : null;
    const [variantSku, setVariantSku] = useState(PRODUCT_LEVEL_SKU);
    const skuChoices = useMemo(() => catalogSkuChoices(product), [product]);
    const isBusy =
      mockupConfigStore.uploadingStorePrintingFiles ||
      mockupConfigStore.loadingStorePrintingFiles;
    const skuSelected = catalogSkuForRequest(variantSku) != null;
    const files = mockupConfigStore.storePrintingFiles.filter((file) =>
      mockupMatchesCatalogSku(file.variant_sku, variantSku),
    );

    useEffect(() => {
      setVariantSku(PRODUCT_LEVEL_SKU);
    }, [productId]);

    useEffect(() => {
      if (!open || !resolvedStoreId || !productId) return;
      void mockupConfigStore
        .fetchStorePrintingFiles(
          {
            store_id: resolvedStoreId,
            product_id: productId,
          },
          { force: true },
        )
        .catch((error: unknown) => {
          const err = error as { response?: { data?: { message?: string } } };
          showError(
            "Failed to load printing files",
            err?.response?.data?.message ||
            "Could not load printing files for this product.",
          );
        });
    }, [open, resolvedStoreId, productId, mockupConfigStore]);

    const handleUpload = useCallback(
      async (nextFiles: File[]) => {
        if (!resolvedStoreId || !productId || !nextFiles.length) return;
        const sku = catalogSkuForRequest(variantSku);
        if (!sku) {
          showError(
            "SKU required",
            "Select a SKU. New printing files are saved against one SKU.",
          );
          return;
        }
        try {
          const uploadedUrls = await new ImageAPI().uploadCdr(nextFiles);
          await mockupConfigStore.saveStorePrintingFiles({
            store_id: resolvedStoreId,
            product_id: productId,
            image_urls: uploadedUrls,
            variant_sku: sku,
          });
          await mockupConfigStore.fetchStorePrintingFiles(
            {
              store_id: resolvedStoreId,
              product_id: productId,
            },
            { force: true },
          );
        } catch (error: unknown) {
          const err = error as { response?: { data?: { message?: string } } };
          showError(
            "Upload failed",
            err?.response?.data?.message || "Failed to upload printing files",
          );
        }
      },
      [resolvedStoreId, productId, mockupConfigStore, variantSku, showError],
    );

    const handleDelete = useCallback(
      async (fileId: number) => {
        if (!resolvedStoreId || !productId) return;
        try {
          await mockupConfigStore.deleteStorePrintingFile({
            id: fileId,
            store_id: resolvedStoreId,
            product_id: productId,
          });
          showSuccess("Printing file deleted successfully");
        } catch (error: unknown) {
          const err = error as { response?: { data?: { message?: string } } };
          showError(
            "Delete failed",
            err?.response?.data?.message || "Failed to delete printing file",
          );
        }
      },
      [resolvedStoreId, productId, mockupConfigStore],
    );

    return (
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (isBusy && reason === "backdropClick") return;
          onClose();
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.16)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background:
                "linear-gradient(90deg, #4F46E5 0%, #7C3AED 50%, #EC4899 100%)",
              zIndex: 1,
            },
          }}
        >
          <DialogTitle
            sx={{
              pr: 7,
              pt: 2.5,
              pb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {product ? (
              <Box
                component="img"
                src={getPackDisplayImage(product)}
                alt=""
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  objectFit: "contain",
                  bgcolor: "rgba(15, 23, 42, 0.03)",
                  border: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(79, 70, 229, 0.08)",
                  flexShrink: 0,
                }}
              >
                <InsertDriveFileOutlinedIcon sx={{ color: "#4F46E5" }} />
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <CustomTypography variant="heading5" fontWeight={700}>
                Printing files
              </CustomTypography>
              <CustomTypography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {product?.label
                  ? `${product.label}${productId ? ` · ID ${productId}` : ""}`
                  : "Upload CDR, PDF, or XLSX files for this product"}
              </CustomTypography>
            </Box>
            {skuChoices.length > 1 && (
              <Box sx={{ width: 220, flexShrink: 0, mr: 4 }}>
                <AppDropDown
                  title="SKU"
                  isMultiSelect={false}
                  list={skuChoices}
                  value={variantSku}
                  onSingleSelectionChange={setVariantSku}
                  placeholderText="All SKUs"
                  width="220px"
                  height="32px"
                  fontSize="0.75rem"
                />
              </Box>
            )}
            <IconButton
              aria-label="Close"
              onClick={onClose}
              disabled={mockupConfigStore.uploadingStorePrintingFiles}
              sx={{
                position: "absolute",
                right: 12,
                top: 12,
                bgcolor: "grey.50",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
        </Box>

        <DialogContent sx={{ pt: 1.5, pb: 3 }}>
          {!resolvedStoreId || !productId ? (
            <CustomTypography variant="body2" color="text.secondary">
              Store or product is missing.
            </CustomTypography>
          ) : (
            <Stack spacing={2.5}>
              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  Upload files
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.25 }}
                >
                  {skuSelected
                    ? "Accepted formats: CDR, PDF, XLSX"
                    : "Select a SKU. New printing files are saved against that SKU."}
                </CustomTypography>
                <Box
                  sx={{
                    width: "100%",
                    "& > div": {
                      width: "100% !important",
                      maxWidth: "100% !important",
                      minWidth: 0,
                    },
                    "& .image-upload-container": {
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    },
                    "& .file-upload-label": {
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    },
                  }}
                >
                  <ImageUpload
                    id={`store-printing-files-${productId}`}
                    minHeight="8.5rem"
                    size="large"
                    allowedFileTypes=".cdr,.pdf,.xlsx"
                    uploadText="Drop your printing files here, or browse"
                    showImagesList={false}
                    multiple
                    existingImageList={[]}
                    addImagesToBeUploaded={(nextFiles) => {
                      void handleUpload(nextFiles);
                    }}
                    disabled={
                      !skuSelected ||
                      mockupConfigStore.uploadingStorePrintingFiles
                    }
                  />
                </Box>
                {mockupConfigStore.uploadingStorePrintingFiles ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1.25 }}
                  >
                    <CircularProgress size={14} />
                    <CustomTypography variant="caption" color="text.secondary">
                      Uploading...
                    </CustomTypography>
                  </Stack>
                ) : null}
              </Box>

              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Uploaded files
                  </CustomTypography>
                  {files.length > 0 ? (
                    <CustomTypography variant="caption" color="text.secondary">
                      {files.length} file{files.length === 1 ? "" : "s"}
                    </CustomTypography>
                  ) : null}
                </Stack>

                {mockupConfigStore.loadingStorePrintingFiles ? (
                  <Box
                    sx={{
                      py: 4,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : files.length > 0 ? (
                  <Stack
                    spacing={1}
                    sx={{
                      maxHeight: 260,
                      overflowY: "auto",
                      pr: 0.5,
                      "&::-webkit-scrollbar": { width: 4 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(79, 70, 229, 0.35)",
                        borderRadius: 4,
                      },
                    }}
                  >
                    {files.map((file) => {
                      const name =
                        fileNameFromUrl(file.image_url) || file.image_url;
                      return (
                        <Stack
                          key={file.id}
                          direction="row"
                          alignItems="center"
                          spacing={1.25}
                          sx={{
                            px: 1.5,
                            py: 1.1,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "rgba(15, 23, 42, 0.08)",
                            bgcolor: "rgba(15, 23, 42, 0.02)",
                          }}
                        >
                          <Box
                            component="img"
                            src={cdricon}
                            alt=""
                            sx={{ width: 22, height: 22, flexShrink: 0 }}
                          />
                          <CustomTypography
                            variant="caption"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={name}
                          >
                            {name}
                          </CustomTypography>
                          {!skuSelected ? (
                            <CustomTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flexShrink: 0 }}
                            >
                              {String(file.variant_sku ?? "").trim() || "Product"}
                            </CustomTypography>
                          ) : null}
                          <Tooltip title="Open" arrow>
                            <IconButton
                              size="small"
                              component="a"
                              href={file.image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open printing file"
                            >
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              aria-label="Delete printing file"
                              onClick={() => void handleDelete(file.id)}
                              disabled={
                                mockupConfigStore.uploadingStorePrintingFiles
                              }
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      py: 3,
                      px: 2,
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      textAlign: "center",
                      bgcolor: "rgba(15, 23, 42, 0.015)",
                    }}
                  >
                    <CustomTypography variant="caption" color="text.secondary">
                      {skuSelected
                        ? "No printing files for this SKU."
                        : "No printing files uploaded yet."}
                    </CustomTypography>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);

export default StorePrintingFilesDialog;
